import React, { useCallback, useMemo, useState } from "react";
import { RedeemIncentivePauseProps } from "..";
import { useSmartrrCustomerDispatch } from "@customer-app/state/typedCustomerReduxHooks";
import { CustomerAction } from "@customer-app/state/typedCustomerRedux";
import { toast } from "react-toastify";
import { localize, typedLocalize, typedLocalizeKey } from "@smartrr/shared/utils/sharedTranslations/localize";
import { unpauseSubscription } from "@customer-app/state/actionCreators/customerPurchaseStates";
import { AquariusButtonGroup } from "@smartrr/shared/customerPortal/components/AquariusButtonGroup";
import { AquariusButton } from "@smartrr/shared/customerPortal/components/AquariusButton";
import { Typography } from "@mui/material";
import { Trans } from "react-i18next";
import { ConfirmCancelButtons } from "@smartrr/shared/customerPortal/components/ConfirmCancelButtons";
import moment from "moment";
import { LoyaltyStoreAccess } from "../../../../../store";
import { ModalState } from "../../../../../utils/constants";

export const RedeemIncentiveUnpauseButtons = ({
  purchaseState,
  closeIncentivesModal,
  incentive,
}: RedeemIncentivePauseProps) => {
  const actions = LoyaltyStoreAccess.useActions();
  const dispatch = useSmartrrCustomerDispatch();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const modalState = LoyaltyStoreAccess.useUnpauseModalState();
  const localizeShorthand = "subscriptions.yourSubscriptions.manageSubscriptionModal";
  const isLoyaltyLoading = LoyaltyStoreAccess.useIsLoading();

  const stringUnpauseDate = moment(purchaseState.unpauseDate).format("MMMM Do");

  const onResume = async () => {
    await dispatch(unpauseSubscription(purchaseState.id)).then(res => {
      if (res.type === "PURCHASE_STATE_UNPAUSE_ERROR") {
        toast.error(localize(`${localizeShorthand}.pauseView.unpauseErrorToast`));
      } else if (res.type === "PURCHASE_STATE_UNPAUSE_SUCCESS") {
        toast(localize(`${localizeShorthand}.pauseView.unpauseToast`));
      }
    });
  };

  const redeemIncentive = async () => {
    const response = await actions.order.redeem.incentive(incentive);
    if (response.result === "success") {
      const info = response.data;
      if (info.orgPoints) {
        dispatch({
          type: "UPDATED_CUSTOMER_LOYALTY_POINTS",
          payload: {
            points: info.orgPoints,
          },
        } satisfies CustomerAction<"UPDATED_CUSTOMER_LOYALTY_POINTS">);
      }
      if (info.updatedCPS) {
        dispatch({
          type: "UPDATE_CUSTOMER_PURCHASE_STATE",
          payload: {
            purchaseState: info.updatedCPS,
          },
        } satisfies CustomerAction<"UPDATE_CUSTOMER_PURCHASE_STATE">);
      }
      toast(
        `${
          incentive.priceRule.type === "fixed_amount"
            ? `$${-incentive.priceRule.value}`
            : `${-incentive.priceRule.value}%`
        } ${typedLocalize("loyalty.addCreditToOrderToDate")}` + stringUnpauseDate
      );
    } else {
      toast.error(response.message);
    }

    closeIncentivesModal();
  };

  const unpauseAndRedeemIncentive = useCallback(async () => {
    setIsLoading(true);
    await onResume();
    await redeemIncentive();
    setIsLoading(false);
  }, []);

  const areButtonsLoading = useMemo(() => isLoading ?? isLoyaltyLoading, [isLoading, isLoyaltyLoading]);

  if (purchaseState.unpauseDate) {
    switch (modalState) {
      case ModalState.REDEEM_IMMEDIATELY:
      case ModalState.REDEEM_UNPAUSE_DATE: {
        return (
          <ConfirmCancelButtons
            isLoading={areButtonsLoading}
            onCancel={() => actions.modal.updateModalState(ModalState.INITIAL_REDEMPTION)}
            onConfirm={modalState === ModalState.REDEEM_IMMEDIATELY ? unpauseAndRedeemIncentive : redeemIncentive}
            disabled={areButtonsLoading}
            cancelTextOverride={"general.backBtn"}
            confirmTextOverride={"general.confirmBtn"}
          />
        );
      }
      default: {
        return (
          <AquariusButtonGroup topBorder>
            <AquariusButton
              onClick={() => actions.modal.updateModalState(ModalState.REDEEM_UNPAUSE_DATE)}
              disabled={areButtonsLoading}
            >
              <Typography variant="body1">
                <Trans
                  i18nKey={typedLocalizeKey("loyalty.resumeOnUnpauseDateButton")}
                  components={[<span key={0} />, <span key={1} />]}
                  values={{
                    date: stringUnpauseDate,
                  }}
                />
              </Typography>
            </AquariusButton>
            <AquariusButton
              filled
              onClick={() => actions.modal.updateModalState(ModalState.REDEEM_IMMEDIATELY)}
              isLoading={areButtonsLoading}
              disabled={areButtonsLoading}
            >
              {typedLocalize("loyalty.resumeImmediatelyButton")}
            </AquariusButton>
          </AquariusButtonGroup>
        );
      }
    }
  }

  return (
    <AquariusButton filled onClick={async () => unpauseAndRedeemIncentive()} isLoading={areButtonsLoading}>
      {typedLocalize("loyalty.resumeImmediatelyButton")}
    </AquariusButton>
  );
};
